import { useEffect, useMemo, useRef, useState } from 'react';
import { getWebApp } from './max.js';
import {
  lookupVin, confirmVin, fetchProfile, saveProfile, fetchHistory, deleteHistory, sendFeedback,
  fetchServiceStations, fetchMiniappFeatureFlags,
  type VinResult, type VinSuccess, type HistoryItem, type ServiceStation, type MiniappFeatureFlags,
} from './api.js';
import {
  filterServiceStations, formatDistance, nearestServiceStations,
  type Coordinates,
} from './serviceStations.js';
import { VIN_PREFIXES, type VinPrefix, cleanTail, normalizeVinParts, TAIL_MAX_LENGTH } from './utils.js';
import logoUrl from './assets/logo.png';
import { FindDealerScreen } from './FindDealerScreen.js';
import { createActivityTracker, type TrackActivity, type ActivitySection } from './activity.js';
import { FindSparePartScreen } from './FindSparePartScreen.js';
import { ExpiredSessionNotice } from './ExpiredSessionNotice.js';
import { VinPicker } from './VinPicker.js';
import { OurContactsScreen } from './OurContactsScreen.js';

type PhoneStatus = 'pending' | 'granted' | 'denied' | 'expired' | 'error';
type Screen = 'home' | 'report-problem' | 'find-service' | 'find-dealer' | 'find-spare-part' | 'our-contacts';
type FeatureAccess = { reportProblem: boolean; findService: boolean; findDealer: boolean; findSparePart: boolean; ourContacts: boolean };
type FeatureKey = 'report_problem' | 'find_service' | 'find_dealer' | 'find_spare_part' | 'our_contacts';
type FeatureCopy = Record<FeatureKey, { title: string; description: string }>;

const DEFAULT_FEATURE_COPY: FeatureCopy = {
  report_problem: { title: 'Сообщить о проблеме', description: 'Найдём изделие по VIN и передадим обращение специалисту' },
  find_service: { title: 'Найти СТО', description: 'Подберём ближайшую станцию технического обслуживания' },
  find_dealer: { title: 'Найти дилера', description: 'Адреса и контакты дилеров ТОНАР по городам и регионам' },
  find_spare_part: { title: 'Найти запчасть', description: 'Отправим запрос специалистам по запасным частям' },
  our_contacts: { title: 'Наши контакты', description: 'Связаться с ТОНАР по бесплатному номеру' },
};

function resolveFeatureCopy(raw: MiniappFeatureFlags['feature_copy']): FeatureCopy {
  const result = {} as FeatureCopy;
  for (const key of Object.keys(DEFAULT_FEATURE_COPY) as FeatureKey[]) {
    const incoming = raw?.[key];
    const title = typeof incoming?.title === 'string' ? incoming.title.trim() : '';
    result[key] = {
      title: title || DEFAULT_FEATURE_COPY[key].title,
      description: typeof incoming?.description === 'string'
        ? incoming.description.trim()
        : DEFAULT_FEATURE_COPY[key].description,
    };
  }
  return result;
}

export function App() {
  // SDK загружается и в обычном браузере. Контекст сохраняем до удаления access из URL.
  const canEnter = useMemo(() => {
    const external = new URLSearchParams(window.location.search).get('mode') === 'stations-nearby';
    const token = new URLSearchParams(window.location.hash.slice(1)).get('access');
    return !!getWebApp()?.initData?.trim() || (external && !!token?.trim());
  }, []);
  return canEnter ? <Miniapp /> : <MaxOnlyScreen />;
}

function MaxOnlyScreen() {
  return <main className="page max-only">
    <img className="page__logo" src={logoUrl} alt="ТОНАР" />
    <section className="max-only__card" aria-labelledby="max-only-title">
      <div className="max-only__icon" aria-hidden="true">
        <svg viewBox="0 0 48 48"><path d="M38 22a14 14 0 0 1-14 14h-5l-9 6 2-12a14 14 0 1 1 26-8Z" /><path d="M17 22h14M17 28h8" /></svg>
      </div>
      <p className="home__eyebrow">Сервисы ТОНАР</p>
      <h1 id="max-only-title">Откройте приложение в MAX</h1>
      <p className="max-only__description">Этот сервис работает как мини-приложение в мессенджере MAX.</p>
      <p className="max-only__hint">Вернитесь в чат с нашим ботом в MAX и нажмите кнопку мини-приложения.</p>
    </section>
  </main>;
}

function Miniapp() {
  const webApp = useMemo(() => getWebApp(), []);
  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const hashParams = useMemo(() => new URLSearchParams(window.location.hash.replace(/^#/, '')), []);
  const externalStationsMode = queryParams.get('mode') === 'stations-nearby';
  const incomingAccessToken = hashParams.get('access') ?? '';
  const [featureAccess, setFeatureAccess] = useState<FeatureAccess>({ reportProblem: true, findService: true, findDealer: false, findSparePart: false, ourContacts: false });
  const [featureCopy, setFeatureCopy] = useState<FeatureCopy>(DEFAULT_FEATURE_COPY);
  const [externalAccessToken, setExternalAccessToken] = useState(incomingAccessToken);
  const [featureFlagsReady, setFeatureFlagsReady] = useState(false);
  const [externalAccessDenied, setExternalAccessDenied] = useState(false);
  const [screen, setScreen] = useState<Screen>(() => externalStationsMode
    ? 'find-service'
    : 'home');
  const [prefix, setPrefix] = useState<VinPrefix>('X0T');
  const [tail, setTail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VinResult | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [phoneStatus, setPhoneStatus] = useState<PhoneStatus>('pending');
  const [profileCheckAttempt, setProfileCheckAttempt] = useState(0);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [repeat, setRepeat] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [commentOpen, setCommentOpen] = useState(false);
  const [pendingFallback, setPendingFallback] = useState<unknown>(null);
  const tailRef = useRef<HTMLInputElement>(null);
  const track = useMemo(() => createActivityTracker(externalStationsMode
    ? { accessToken: externalAccessToken } : { initData: webApp?.initData }), [externalStationsMode, externalAccessToken, webApp]);
  const lastVisited = useRef<Screen | null>(null);
  useEffect(() => {
    if (!featureFlagsReady || externalAccessDenied) return;
    if (screen === 'home') { lastVisited.current = 'home'; return; }
    const section = screen.replaceAll('-', '_') as ActivitySection;
    const enabled = screen === 'report-problem' ? featureAccess.reportProblem : screen === 'find-service' ? featureAccess.findService
      : screen === 'find-dealer' ? featureAccess.findDealer : screen === 'find-spare-part' ? featureAccess.findSparePart
        : featureAccess.ourContacts;
    if (!enabled || lastVisited.current === screen) return;
    lastVisited.current = screen;
    track(section, 'open');
  }, [screen, featureFlagsReady, externalAccessDenied, featureAccess, track]);

  useEffect(() => {
    let cancelled = false;
    fetchMiniappFeatureFlags(
      webApp?.initData ?? '',
      incomingAccessToken,
      webApp?.initDataUnsafe.user?.id,
    )
      .then((flags) => {
        if (cancelled) return;
        if (externalStationsMode && !webApp?.initData && flags.identity_source !== 'access_token') {
          setExternalAccessDenied(true);
          setFeatureAccess({ reportProblem: false, findService: false, findDealer: false, findSparePart: false, ourContacts: false });
          return;
        }
        const access = {
          reportProblem: !externalStationsMode && flags.report_problem,
          findService: flags.find_service,
          findDealer: !externalStationsMode && flags.find_dealer === true,
          findSparePart: !externalStationsMode && flags.find_spare_part === true,
          ourContacts: !externalStationsMode && flags.our_contacts === true,
        };
        setFeatureAccess(access);
        setFeatureCopy(resolveFeatureCopy(flags.feature_copy));
        if (flags.external_access_token) setExternalAccessToken(flags.external_access_token);
        if (!externalStationsMode) {
          const availableScreens: Screen[] = [];
          if (access.reportProblem) availableScreens.push('report-problem');
          if (access.findService) availableScreens.push('find-service');
          if (access.findDealer) availableScreens.push('find-dealer');
          if (access.findSparePart) availableScreens.push('find-spare-part');
          if (access.ourContacts) availableScreens.push('our-contacts');
          setScreen(availableScreens.length === 1 ? availableScreens[0]! : 'home');
        }
      })
      .catch(() => {
        if (cancelled) return;
        // При недоступности настройки сохраняем прежний VIN-сценарий;
        // это не блокирует основную функцию приложения.
        if (!externalStationsMode) {
          setFeatureAccess({ reportProblem: true, findService: false, findDealer: false, findSparePart: false, ourContacts: false });
          setScreen('report-problem');
        } else {
          setExternalAccessDenied(true);
          setFeatureAccess({ reportProblem: false, findService: false, findDealer: false, findSparePart: false, ourContacts: false });
        }
      })
      .finally(() => {
        if (!cancelled) setFeatureFlagsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [externalStationsMode, incomingAccessToken, webApp]);

  useEffect(() => {
    if (!externalStationsMode || !incomingAccessToken || !window.location.hash) return;
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
  }, [externalStationsMode, incomingAccessToken]);

  const availableFeatureCount = Number(featureAccess.reportProblem) + Number(featureAccess.findService) + Number(featureAccess.findDealer)
    + Number(featureAccess.findSparePart) + Number(featureAccess.ourContacts);
  const showHomeNavigation = !externalStationsMode && availableFeatureCount > 1;

  const loadHistory = () => {
    const initData = webApp?.initData;
    if (!initData) return;
    fetchHistory(initData)
      .then((res) => setHistory(res.items ?? []))
      .catch(() => {});
  };

  const onSendFeedback = (requestId: string, answer: 'resolved' | 'unresolved') => {
    const initData = webApp?.initData;
    if (!initData) return;
    // Оптимистично обновляем локально, потом синхронизируем с сервером.
    setHistory((items) => items.map((it) => it.request_id === requestId ? { ...it, feedback: answer } : it));
    sendFeedback(initData, requestId, answer)
      .then(() => loadHistory())
      .catch(() => loadHistory());
  };

  const onDeleteHistory = (id: number) => {
    const initData = webApp?.initData;
    if (!initData) return;
    setHistory((items) => items.filter((it) => it.id !== id)); // оптимистично
    deleteHistory(initData, id)
      .then((res) => setHistory(res.items ?? []))
      .catch(() => loadHistory());
  };

  const confirmDelete = () => {
    if (confirmDeleteId !== null) onDeleteHistory(confirmDeleteId);
    setConfirmDeleteId(null);
  };

  useEffect(() => {
    if (!featureFlagsReady || !featureAccess.reportProblem || screen !== 'report-problem') return;

    tailRef.current?.focus();
    const initData = webApp?.initData;

    // Нет initData (открыто вне MAX) — работаем без телефона.
    if (!initData) {
      setPhoneStatus('granted');
      return;
    }

    loadHistory();

    const requestContact = () => {
      const contactPromise = webApp?.requestContact?.();
      if (!contactPromise) {
        // SDK без requestContact — разрешаем работу.
        setPhoneStatus('granted');
        return;
      }
      contactPromise
        .then((contact) => {
          if (contact?.phone) {
            setPhone(contact.phone);
            setPhoneStatus('granted');
            // Запоминаем телефон, чтобы не спрашивать в следующий раз.
            saveProfile(initData, contact.phone).catch(() => {});
          } else {
            setPhoneStatus('denied');
          }
        })
        .catch(() => setPhoneStatus('denied'));
    };

    // Сначала узнаём пользователя по max_user_id — вдруг телефон уже сохранён.
    fetchProfile(initData)
      .then((profile) => {
        if (!profile.ok) {
          setPhoneStatus(profile.error?.code === 'EXPIRED' ? 'expired' : 'error');
        } else if (profile.phone) {
          setPhone(profile.phone);
          setPhoneStatus('granted');
        } else {
          requestContact();
        }
      })
      .catch(() => setPhoneStatus('error'));
  }, [featureAccess.reportProblem, featureFlagsReady, profileCheckAttempt, screen]);

  // Пока мини-апп открыт — подтягиваем статусы истории (например, "В работе"),
  // чтобы не ждать перезапуска. Опрос только когда приложение на экране.
  useEffect(() => {
    if (!featureFlagsReady || !featureAccess.reportProblem || screen !== 'report-problem') return;

    const refresh = () => {
      if (document.visibilityState === 'visible') {
        loadHistory();
      }
    };
    const id = window.setInterval(refresh, 10000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [featureAccess.reportProblem, featureFlagsReady, screen]);

  useEffect(() => {
    const backButton = webApp?.BackButton;
    if (!backButton || !showHomeNavigation) return;

    if (screen === 'home') {
      backButton.hide();
      return;
    }

    const goHome = () => setScreen('home');
    backButton.show();
    backButton.onClick(goHome);
    return () => {
      backButton.offClick(goHome);
      backButton.hide();
    };
  }, [screen, showHomeNavigation, webApp]);

  const onTailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTail(cleanTail(e.target.value));
    setResult(null);
    setRepeat(false);
  };

  const acceptFullVin = (raw: string): boolean => {
    const normalized = normalizeVinParts(prefix, raw);
    if (!normalized.fullVinDetected) return false;
    setPrefix(normalized.prefix);
    setTail(normalized.tail);
    setHint('Полный VIN распознан, префикс выбран автоматически.');
    setResult(null);
    setRepeat(false);
    return true;
  };

  const runLookup = async (vin: string) => {
    if (!webApp?.initData) {
      setResult({ ok: false, error: { code: 'NO_INIT_DATA', message: 'Откройте мини-приложение из бота MAX.' } });
      return;
    }
    track('report_problem', 'vin_search');
    setLoading(true);
    try {
      const res = await lookupVin(vin, webApp.initData);
      setResult(res);
      webApp.HapticFeedback?.notificationOccurred(res.ok ? 'success' : 'error');
      // VIN найден — сразу открываем модал комментария. Получателя (менеджер
      // или чат из настроек) определит сервер при отправке.
      if (res.ok) {
        onRequestSend(buildFallback(res));
      }
    } catch (err) {
      setResult({
        ok: false,
        error: { code: 'NETWORK', message: err instanceof Error ? err.message : 'Сетевая ошибка' },
      });
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tail.length === 0) {
      setHint('Введите символы после префикса.');
      setResult(null);
      return;
    }
    const normalized = normalizeVinParts(prefix, tail);
    if (normalized.fullVinDetected) {
      setPrefix(normalized.prefix);
      setTail(normalized.tail);
    }
    setHint(null);
    setRepeat(false);
    void runLookup(normalized.vin);
  };

  // Повтор обращения из истории: подставляем VIN и запускаем обычный поиск.
  const repeatRequest = (vin: string) => {
    if (loading) return;
    const matched = VIN_PREFIXES.find((p) => vin.startsWith(p));
    if (matched) {
      setPrefix(matched);
      setTail(vin.slice(matched.length));
    } else {
      setTail(vin);
    }
    setHint(null);
    setRepeat(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    void runLookup(vin);
  };

  const selectSavedVin = (vin: string) => {
    const matched = VIN_PREFIXES.find((item) => vin.startsWith(item));
    if (!matched) return;
    setPrefix(matched);
    setTail(vin.slice(matched.length));
    setHint(null);
    setResult(null);
    setRepeat(false);
  };

  const enterAnotherVin = () => {
    setTail('');
    setHint(null);
    setResult(null);
    setRepeat(false);
    setTimeout(() => tailRef.current?.focus(), 0);
  };

  const onReset = () => {
    setResult(null);
    setTail('');
    setHint(null);
    setRepeat(false);
    setTimeout(() => tailRef.current?.focus(), 0);
  };

  // Открываем нижний модал для ввода комментария.
  const onRequestSend = (fallback: unknown) => {
    setPendingFallback(fallback);
    setComment('');
    setCommentOpen(true);
  };

  // Отправка уведомления в Bitrix (после ввода комментария в модале).
  const onSend = async (fallback: unknown, commentText: string) => {
    if (!webApp?.initData) return;
    setLoading(true);
    try {
      const res = await confirmVin(prefix + tail, webApp.initData, fallback, phone, commentText || null, repeat);
      webApp.HapticFeedback?.notificationOccurred(res.ok ? 'success' : 'error');
      setResult(res);
      if (res.ok) {
        track('report_problem', 'report_submit');
        setTail('');
        loadHistory();
      }
    } catch (err) {
      setResult({
        ok: false,
        error: { code: 'NETWORK', message: err instanceof Error ? err.message : 'Сетевая ошибка' },
      });
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setLoading(false);
    }
  };

  const submitComment = () => {
    setCommentOpen(false);
    void onSend(pendingFallback, comment);
  };

  if (externalAccessDenied) return <MaxOnlyScreen />;

  return (
    <main className="page">
      <header className="page__header">
        {showHomeNavigation && screen !== 'home' ? (
          <button className="page__back" type="button" onClick={() => setScreen('home')} aria-label="На главный экран">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        ) : null}
        <img className="page__logo" src={logoUrl} alt="" />
      </header>

      {!featureFlagsReady ? (
        <p className="feature-loading">Загружаем…</p>
      ) : screen === 'home' ? (
        <HomeScreen
          reportProblemEnabled={featureAccess.reportProblem}
          findServiceEnabled={featureAccess.findService}
          findDealerEnabled={featureAccess.findDealer}
          findSparePartEnabled={featureAccess.findSparePart}
          ourContactsEnabled={featureAccess.ourContacts}
          featureCopy={featureCopy}
          onReportProblem={() => setScreen('report-problem')}
          onFindService={() => setScreen('find-service')}
          onFindDealer={() => setScreen('find-dealer')}
          onFindSparePart={() => setScreen('find-spare-part')}
          onOurContacts={() => setScreen('our-contacts')}
        />
      ) : screen === 'find-service' ? (
        featureAccess.findService
          ? (
            <FindServiceScreen
              webApp={webApp}
              externalLocationMode={externalStationsMode}
              externalAccessToken={externalAccessToken}
              track={track}
              title={featureCopy.find_service.title}
            />
          )
          : <FeatureUnavailable />
      ) : screen === 'find-dealer' ? (
        featureAccess.findDealer ? <FindDealerScreen webApp={webApp} track={track} title={featureCopy.find_dealer.title} /> : <FeatureUnavailable />
      ) : screen === 'find-spare-part' ? (
        featureAccess.findSparePart ? <FindSparePartScreen webApp={webApp} track={track} title={featureCopy.find_spare_part.title} onDone={() => setScreen('home')} /> : <FeatureUnavailable />
      ) : screen === 'our-contacts' ? (
        featureAccess.ourContacts ? <OurContactsScreen track={track} title={featureCopy.our_contacts.title} /> : <FeatureUnavailable />
      ) : !featureAccess.reportProblem ? (
        <FeatureUnavailable />
      ) : phoneStatus === 'expired' ? (
        <ExpiredSessionNotice webApp={webApp} className="phone-denied phone-denied--action" />
      ) : phoneStatus === 'error' ? (
        <div className="phone-denied" role="alert">
          <p>Не удалось проверить сохранённый номер.</p>
          <button type="button" onClick={() => { setPhoneStatus('pending'); setProfileCheckAttempt((attempt) => attempt + 1); }}>Повторить</button>
        </div>
      ) : phoneStatus === 'denied' ? (
        <p className="phone-denied">
          Для работы необходимо поделиться номером телефона. Перезапустите приложение и разрешите доступ к номеру.
        </p>
      ) : (
        <>
          <h1 className="feature-title">{featureCopy.report_problem.title}</h1>
          <form className="form" onSubmit={onSubmit}>
            <label className="form__label" htmlFor="vin-tail">
              Укажите VIN
            </label>
            <div className="form__vin-row">
              <select
                className="form__prefix"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value as VinPrefix)}
                disabled={loading || phoneStatus === 'pending'}
                aria-label="Префикс VIN"
              >
                {VIN_PREFIXES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <input
                ref={tailRef}
                id="vin-tail"
                className="form__input form__tail"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={TAIL_MAX_LENGTH}
                value={tail}
                onChange={onTailChange}
                onPaste={(e) => {
                  if (acceptFullVin(e.clipboardData.getData('text'))) e.preventDefault();
                }}
                onBlur={(e) => { acceptFullVin(e.currentTarget.value); }}
                disabled={loading || phoneStatus === 'pending'}
              />
            </div>
            <VinPicker
              initData={webApp?.initData ?? ''}
              currentVin={tail ? prefix + tail : ''}
              refreshKey={history[0]?.request_id ?? history.length}
              onSelect={selectSavedVin}
              onManualEntry={enterAnotherVin}
            />
            {hint ? <p className="form__hint">{hint}</p> : null}
            {result?.ok !== true && (
              <button
                className="form__submit"
                type="submit"
                disabled={loading || tail.length === 0 || phoneStatus === 'pending'}
              >
                {loading ? 'Ищем…' : phoneStatus === 'pending' ? 'Ожидание…' : 'Найти'}
              </button>
            )}
          </form>

          {result ? (
            <ResultCard result={result} loading={loading} onRequestSend={onRequestSend} onReset={onReset} />
          ) : null}

          {history.length > 0 && (
            <section className="history">
              <h2 className="history__title">
                История обращений
                <span className="history__hint">смахните запись до края, чтобы удалить</span>
              </h2>
              <ul className="history__list">
                {history.map((item) => (
                  <HistoryRow
                    key={item.id}
                    item={item}
                    loading={loading}
                    pendingDelete={confirmDeleteId === item.id}
                    onRepeat={repeatRequest}
                    onDelete={setConfirmDeleteId}
                    onFeedback={onSendFeedback}
                  />
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {confirmDeleteId !== null && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title">Удалить запись из истории?</h2>
            <div className="modal__actions">
              <button className="modal__cancel" onClick={() => setConfirmDeleteId(null)}>
                Отмена
              </button>
              <button className="modal__danger" onClick={confirmDelete}>
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {commentOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title">Опишите Вашу проблему</h2>
            <textarea
              className="modal__textarea"
              placeholder="Необязательно"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              autoFocus
            />
            <div className="modal__actions">
              <button className="modal__cancel" onClick={() => setCommentOpen(false)} disabled={loading}>
                Отмена
              </button>
              <button className="modal__submit" onClick={submitComment} disabled={loading}>
                Отправить
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function HomeScreen({
  reportProblemEnabled,
  findServiceEnabled,
  findDealerEnabled,
  findSparePartEnabled,
  ourContactsEnabled,
  featureCopy,
  onReportProblem,
  onFindService,
  onFindDealer,
  onFindSparePart,
  onOurContacts,
}: {
  reportProblemEnabled: boolean;
  findServiceEnabled: boolean;
  findDealerEnabled: boolean;
  findSparePartEnabled: boolean;
  ourContactsEnabled: boolean;
  featureCopy: FeatureCopy;
  onReportProblem: () => void;
  onFindService: () => void;
  onFindDealer: () => void;
  onFindSparePart: () => void;
  onOurContacts: () => void;
}) {
  return (
    <section className="home" aria-labelledby="home-title">
      <p className="home__eyebrow">Сервисы ТОНАР</p>
      <h1 className="home__title" id="home-title">Чем можем помочь?</h1>
      <p className="home__subtitle">Выберите нужный раздел</p>

      <div className="home__services">
        {reportProblemEnabled ? <button className="service-card" type="button" onClick={onReportProblem}>
          <span className="service-card__icon service-card__icon--problem" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 3a9 9 0 1 0 9 9 9 9 0 0 0-9-9Z" />
              <path d="M12 7v6" />
              <path d="M12 17h.01" />
            </svg>
          </span>
          <span className="service-card__body">
            <strong>{featureCopy.report_problem.title}</strong>
            {featureCopy.report_problem.description ? <small>{featureCopy.report_problem.description}</small> : null}
          </span>
          <span className="service-card__arrow" aria-hidden="true">›</span>
        </button> : null}

        {findServiceEnabled ? <button className="service-card" type="button" onClick={onFindService}>
          <span className="service-card__icon service-card__icon--service" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
          </span>
          <span className="service-card__body">
            <strong>{featureCopy.find_service.title}</strong>
            {featureCopy.find_service.description ? <small>{featureCopy.find_service.description}</small> : null}
          </span>
          <span className="service-card__arrow" aria-hidden="true">›</span>
        </button> : null}
        {findDealerEnabled ? <button className="service-card" type="button" onClick={onFindDealer}>
          <span className="service-card__icon service-card__icon--dealer" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M3 10h18l-2-6H5l-2 6Zm2 0v10h14V10M9 20v-6h6v6M3 10v2h18v-2" /></svg>
          </span>
          <span className="service-card__body"><strong>{featureCopy.find_dealer.title}</strong>
            {featureCopy.find_dealer.description ? <small>{featureCopy.find_dealer.description}</small> : null}</span>
          <span className="service-card__arrow" aria-hidden="true">›</span>
        </button> : null}
        {findSparePartEnabled ? <button className="service-card" type="button" onClick={onFindSparePart}>
          <span className="service-card__icon service-card__icon--spare" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M14.5 6.5a4.5 4.5 0 0 0-6 6L3 18l3 3 5.5-5.5a4.5 4.5 0 0 0 6-6l-3 3-3-3 3-3Z" /></svg>
          </span>
          <span className="service-card__body"><strong>{featureCopy.find_spare_part.title}</strong>
            {featureCopy.find_spare_part.description ? <small>{featureCopy.find_spare_part.description}</small> : null}</span>
          <span className="service-card__arrow" aria-hidden="true">›</span>
        </button> : null}
        {ourContactsEnabled ? <button className="service-card" type="button" onClick={onOurContacts}>
          <span className="service-card__icon service-card__icon--contacts" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M7 3h3l1.5 4-2 1.5a14 14 0 0 0 6 6l1.5-2 4 1.5v3a4 4 0 0 1-4 4C9.3 21 3 14.7 3 7a4 4 0 0 1 4-4Z" /></svg>
          </span>
          <span className="service-card__body"><strong>{featureCopy.our_contacts.title}</strong>
            {featureCopy.our_contacts.description ? <small>{featureCopy.our_contacts.description}</small> : null}</span>
          <span className="service-card__arrow" aria-hidden="true">›</span>
        </button> : null}
        {!reportProblemEnabled && !findServiceEnabled && !findDealerEnabled && !findSparePartEnabled && !ourContactsEnabled
          ? <p className="home__empty">Все разделы временно отключены.</p>
          : null}
      </div>
    </section>
  );
}

function FeatureUnavailable() {
  return (
    <section className="feature-unavailable">
      <h1>Раздел временно недоступен</h1>
      <p>Попробуйте открыть мини-приложение позже.</p>
    </section>
  );
}

const SERVICE_STATIONS_PAGE_SIZE = 10;

function FindServiceScreen({
  webApp,
  externalLocationMode,
  externalAccessToken,
  track,
  title = DEFAULT_FEATURE_COPY.find_service.title,
}: {
  webApp: ReturnType<typeof getWebApp>;
  externalLocationMode: boolean;
  externalAccessToken: string;
  track: TrackActivity;
  title?: string;
}) {
  const [stations, setStations] = useState<ServiceStation[]>([]);
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState<Coordinates | null>(null);
  const [loadingStations, setLoadingStations] = useState(true);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(SERVICE_STATIONS_PAGE_SIZE);
  const queryInputRef = useRef<HTMLInputElement>(null);
  const lastTrackedQuery = useRef('');

  useEffect(() => {
    let cancelled = false;
    setLoadingStations(true);
    fetchServiceStations()
      .then((response) => {
        if (!cancelled) setStations(response.items);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : 'Не удалось загрузить список СТО.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingStations(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const results = useMemo<Array<ServiceStation & { distanceKm?: number }>>(() => {
    if (location) return nearestServiceStations(stations, location);
    return filterServiceStations(stations, query);
  }, [location, query, stations]);
  const visibleStations = results.slice(0, visibleCount);
  const hasSearch = query.trim().length >= 2;
  const hasResultsMode = location !== null || hasSearch;
  const recordSearch = () => {
    const normalized = query.trim().toLocaleLowerCase('ru');
    if (normalized.length < 2 || loadingStations || error || location || lastTrackedQuery.current === normalized) return;
    lastTrackedQuery.current = normalized;
    track('find_service', 'search');
  };
  useEffect(() => {
    if (query.trim().length < 2) { lastTrackedQuery.current = ''; return; }
    const timer = setTimeout(recordSearch, 800);
    return () => clearTimeout(timer);
  }, [query, loadingStations, error, location, track]);

  const onSearchChange = (value: string) => {
    setQuery(value);
    setLocation(null);
    setLocationError(null);
    setVisibleCount(SERVICE_STATIONS_PAGE_SIZE);
  };

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    recordSearch();
    queryInputRef.current?.blur();
  };

  const findNearby = () => {
    setLocationError(null);
    track('find_service', externalLocationMode ? 'nearby_request' : 'nearby_open');

    if (!externalLocationMode) {
      const externalUrl = new URL(window.location.origin);
      externalUrl.searchParams.set('mode', 'stations-nearby');
      if (externalAccessToken) {
        externalUrl.hash = new URLSearchParams({ access: externalAccessToken }).toString();
      }
      webApp?.HapticFeedback?.impactOccurred('light');
      if (webApp?.openLink) {
        webApp.openLink(externalUrl.toString());
      } else {
        window.open(externalUrl.toString(), '_blank', 'noopener,noreferrer');
      }
      return;
    }

    if (!navigator.geolocation) {
      setLocationError('На этом устройстве геолокация недоступна. Введите город или регион.');
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setQuery('');
        setVisibleCount(SERVICE_STATIONS_PAGE_SIZE);
        setLocating(false);
        webApp?.HapticFeedback?.notificationOccurred('success');
      },
      (positionError) => {
        const message = positionError.code === positionError.PERMISSION_DENIED
          ? 'Доступ к геопозиции запрещён. Введите город или регион.'
          : positionError.code === positionError.TIMEOUT
            ? 'Не удалось быстро определить местоположение. Попробуйте ещё раз или воспользуйтесь поиском.'
            : 'Не удалось определить местоположение. Введите город или регион.';
        setLocationError(message);
        setLocating(false);
        webApp?.HapticFeedback?.notificationOccurred('warning');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  const buildRouteUrl = (station: ServiceStation) => {
    const destination = `${station.latitude},${station.longitude}`;
    const routePoints = location
      ? `${location.latitude},${location.longitude}~${destination}`
      : `~${destination}`;
    const url = new URL('https://yandex.ru/maps/');
    url.searchParams.set('mode', 'routes');
    url.searchParams.set('rtext', routePoints);
    url.searchParams.set('rtt', 'auto');
    return url.toString();
  };

  const openRoute = (event: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    // SDK создаёт window.WebApp и в обычном браузере, но его openLink там
    // ничего не открывает. Во внешнем режиме оставляем переход самому <a>.
    if (!externalLocationMode && webApp?.initData && webApp.openLink) {
      event.preventDefault();
      webApp.openLink(url);
    }
  };

  return (
    <section className="station-search" aria-labelledby="find-service-title">
      <h1 className="station-search__title" id="find-service-title">{title}</h1>
      <p className="station-search__subtitle">Найдите ближайшую станцию или укажите город и регион</p>

      <button
        className="station-search__nearby"
        type="button"
        onClick={findNearby}
        disabled={loadingStations || locating}
      >
        <span className="station-search__nearby-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
        </span>
        <span>
          <strong>
            {locating
              ? 'Определяем местоположение…'
              : externalLocationMode ? 'Определить местоположение' : 'Найти рядом со мной'}
          </strong>
          <small>
            {externalLocationMode
              ? 'Браузер запросит доступ к геопозиции'
              : 'Откроется во внешнем браузере'}
          </small>
        </span>
      </button>

      <div className="station-search__divider"><span>или</span></div>

      <label className="station-search__label" htmlFor="station-query">Город, регион или вид работ</label>
      <form className="station-search__input-wrap" onSubmit={submitSearch}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-4-4" />
        </svg>
        <input
          ref={queryInputRef}
          id="station-query"
          className="station-search__input"
          type="search"
          enterKeyHint="search"
          value={query}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Например, Москва"
          autoComplete="off"
          disabled={loadingStations}
        />
      </form>

      {loadingStations ? <p className="station-search__message">Загружаем список СТО…</p> : null}
      {error ? <p className="station-search__message station-search__message--error">{error}</p> : null}
      {locationError ? <p className="station-search__message station-search__message--warn">{locationError}</p> : null}
      {!loadingStations && !error && stations.length === 0
        ? <p className="station-search__message">Список СТО пока не загружен.</p>
        : null}

      {hasResultsMode && !loadingStations && !error ? (
        <section className="station-results" aria-live="polite">
          <div className="station-results__heading">
            <h2>{location ? 'Ближайшие СТО' : 'Результаты поиска'}</h2>
            <span>{results.length}</span>
          </div>
          {location ? <p className="station-results__note">Расстояние указано по прямой</p> : null}

          {visibleStations.length === 0 ? (
            <p className="station-search__message">Ничего не найдено. Попробуйте изменить запрос.</p>
          ) : (
            <ul className="station-results__list">
              {visibleStations.map((station) => (
                <ServiceStationCard
                  key={station.id}
                  station={station}
                  routeUrl={buildRouteUrl(station)}
                  onOpenRoute={openRoute}
                  track={track}
                />
              ))}
            </ul>
          )}

          {visibleCount < results.length ? (
            <button
              className="station-results__more"
              type="button"
              onClick={() => setVisibleCount((count) => count + SERVICE_STATIONS_PAGE_SIZE)}
            >
              Показать ещё
            </button>
          ) : null}
        </section>
      ) : null}
    </section>
  );
}

function ServiceStationCard({
  station,
  routeUrl,
  onOpenRoute,
  track,
}: {
  station: ServiceStation & { distanceKm?: number };
  routeUrl: string;
  onOpenRoute: (event: React.MouseEvent<HTMLAnchorElement>, url: string) => void;
  track: TrackActivity;
}) {
  const [phonesOpen, setPhonesOpen] = useState(false);
  const phoneLinks = station.phones.map((phone) => ({
    label: phone,
    href: `tel:${phone.replace(/[^\d+]/g, '')}`,
  }));
  const phonePickerTitleId = `phone-picker-title-${station.id}`;

  return (
    <li className="station-card">
      <div className="station-card__topline">
        <h3>{station.organization}</h3>
        {station.distanceKm !== undefined
          ? <span className="station-card__distance">{formatDistance(station.distanceKm)}</span>
          : null}
      </div>
      <p className="station-card__address">{station.address}</p>
      {station.types.length > 0 ? (
        <div className="station-card__types">
          {station.types.map((type) => <span key={type}>{type}</span>)}
        </div>
      ) : null}
      <div className="station-card__actions">
        {phoneLinks.length === 1 ? <a href={phoneLinks[0]!.href} onClick={() => track('find_service', 'phone_click', String(station.id))}>Позвонить</a> : null}
        {phoneLinks.length > 1 ? (
          <button type="button" onClick={() => { track('find_service', 'phone_picker', String(station.id)); setPhonesOpen(true); }}>Позвонить</button>
        ) : null}
        <a
          href={routeUrl}
          onClick={(event) => { track('find_service', 'route_click', String(station.id)); onOpenRoute(event, routeUrl); }}
        >
          Маршрут
        </a>
      </div>

      {phonesOpen ? (
        <div className="modal-overlay" onClick={() => setPhonesOpen(false)}>
          <div
            className="modal station-phone-picker"
            role="dialog"
            aria-modal="true"
            aria-labelledby={phonePickerTitleId}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className="modal__title" id={phonePickerTitleId}>Выберите номер</h2>
            <div className="station-phone-picker__list">
              {phoneLinks.map((phone, index) => (
                <a
                  className="station-phone-picker__number"
                  href={phone.href}
                  key={`${phone.label}-${index}`}
                  onClick={() => { track('find_service', 'phone_click', String(station.id)); setPhonesOpen(false); }}
                >
                  {phone.label}
                </a>
              ))}
            </div>
            <button className="modal__cancel" type="button" onClick={() => setPhonesOpen(false)}>Отмена</button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function formatDate(s: string): string {
  // "2026-05-25 12:13:45" → "25.05.2026 12:13"
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (!m) return s;
  return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
}

const SWIPE_MAX = 120;      // максимальный сдвиг карточки, px
const SWIPE_TRIGGER = 90;   // доведён до края — спрашиваем удаление; иначе возврат

function HistoryRow({
  item,
  loading,
  pendingDelete,
  onRepeat,
  onDelete,
  onFeedback,
}: {
  item: HistoryItem;
  loading: boolean;
  pendingDelete: boolean;
  onRepeat: (vin: string) => void;
  onDelete: (id: number) => void;
  onFeedback: (requestId: string, answer: 'resolved' | 'unresolved') => void;
}) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);

  // Пока открыт диалог подтверждения по этой записи — держим карточку отъехавшей.
  // Диалог закрыт (отмена) — возвращаем на место.
  useEffect(() => {
    if (!pendingDelete) setDx(0);
  }, [pendingDelete]);

  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    startX.current = touch.clientX;
    setDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    let next = touch.clientX - startX.current;
    if (next > SWIPE_MAX) next = SWIPE_MAX;
    if (next < -SWIPE_MAX) next = -SWIPE_MAX;
    setDx(next);
  };

  const onTouchEnd = () => {
    setDragging(false);
    if (Math.abs(dx) >= SWIPE_TRIGGER) {
      // Доведён до края — фиксируем карточку открытой и открываем подтверждение.
      setDx(dx < 0 ? -SWIPE_MAX : SWIPE_MAX);
      onDelete(item.id);
    } else {
      setDx(0); // недоведён — возврат на место
    }
  };

  const armed = Math.abs(dx) >= SWIPE_TRIGGER;

  return (
    <li className="history__item">
      <div className={armed ? 'history__delete history__delete--armed' : 'history__delete'} aria-hidden="true">
        <span>Удалить</span>
        <span>Удалить</span>
      </div>
      <div
        className="history__card"
        style={{ transform: `translateX(${dx}px)`, transition: dragging ? 'none' : 'transform .18s ease' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="history__row">
          <span className="history__vin">{item.request_no ? `№${item.request_no} · ${item.vin}` : item.vin}</span>
          <span className="history__date">{formatDate(item.created_at)}</span>
        </div>
        <span className={'history__status' + (
          item.status === 'in_progress' ? ' history__status--work'
          : item.status === 'done' && item.feedback === 'resolved' ? ' history__status--done'
          : item.status === 'done' && item.feedback === 'unresolved' ? ' history__status--warn'
          : item.status === 'done' ? ' history__status--done'
          : ''
        )}>
          {item.status === 'in_progress' ? 'В работе, скоро с Вами свяжутся'
            : item.status === 'done' && item.feedback === 'resolved' ? 'Завершён · проблема решена'
            : item.status === 'done' && item.feedback === 'unresolved' ? 'Завершён · проблема не решена'
            : item.status === 'done' ? 'Завершён'
            : 'Запрос отправлен'}
        </span>
        {item.status === 'done' && item.feedback === null && (
          <div className="history__feedback">
            <p className="history__feedback-q">Ваша проблема решена?</p>
            <div className="history__feedback-row">
              <button
                className="history__feedback-btn history__feedback-btn--yes"
                onClick={() => onFeedback(item.request_id, 'resolved')}
                disabled={loading}
              >Да</button>
              <button
                className="history__feedback-btn history__feedback-btn--no"
                onClick={() => onFeedback(item.request_id, 'unresolved')}
                disabled={loading}
              >Нет</button>
            </div>
          </div>
        )}
        {item.comment ? <p className="history__comment">{item.comment}</p> : null}
        <button
          className="history__repeat"
          onClick={() => onRepeat(item.vin)}
          disabled={loading}
        >
          Сообщить о новой проблеме
        </button>
      </div>
    </li>
  );
}

function ResultCard({
  result,
  loading,
  onRequestSend,
  onReset,
}: {
  result: VinResult;
  loading: boolean;
  onRequestSend: (fallback: unknown) => void;
  onReset: () => void;
}) {
  if (!result.ok) {
    // VIN не найден, но запрос можно отправить (есть fallback) — не критично,
    // показываем как нейтральное предупреждение, а не красную "Ошибку".
    if (result.fallback !== undefined) {
      return (
        <section className="result result--warn">
          <p className="result__warn-text">{result.error.message}</p>
          <button
            className="result__confirm"
            onClick={() => onRequestSend(result.fallback)}
            disabled={loading}
          >
            {loading ? 'Отправляем…' : 'VIN точно введён корректно'}
          </button>
        </section>
      );
    }

    // Настоящая ошибка без возможности отправки.
    return (
      <section className="result result--error">
        <h2>Ошибка</h2>
        <p>{result.error.message}</p>
      </section>
    );
  }

  // notified_via != null — отправка уже выполнялась (lookup возвращает null).
  // Получателя определяет сервер (менеджер или чат из настроек), поэтому
  // на этапе lookup всегда даём отправить.
  const sent = result.notified_via != null;

  if (!sent) {
    return (
      <section className="result result--ok">
        <p className="result__notice result__notice--ok">VIN номер корректный.</p>
        <button
          className="result__confirm"
          onClick={() => onRequestSend(buildFallback(result))}
          disabled={loading}
        >
          {loading ? 'Отправляем…' : 'Отправить запрос'}
        </button>
      </section>
    );
  }

  // Финальное состояние: запрос отправлен либо получателя нет — показываем "Добавить ещё".
  // VIN не дублируем: он виден в истории (после отправки) или в поле формы.
  return (
    <section className="result result--ok">
      <p className="result__notice">{renderNotice(result)}</p>
      <button className="result__reset" onClick={onReset}>
        Добавить ещё
      </button>
    </section>
  );
}

/** Собирает fallback-объект из успешного lookup-ответа для передачи в confirm.php */
function buildFallback(result: VinSuccess): unknown {
  return {
    manager: result.manager,
    chat: result.chat ?? null,
    counterparty: result.counterparty ?? null,
    article: result.article ?? null,
    name: result.name ?? null,
    data: result.data,
  };
}

function renderNotice(result: VinSuccess): string {
  // Получателя нет (ни менеджера, ни чата в настройках) — отправлять было некому.
  if (result.notified_via === 'none') return 'Получатель не определён — уведомление не отправлено.';
  // Попытка отправки была. Даже если Bitrix её отклонил — пользователю показываем успех,
  // детали ошибки доставки остаются в серверном логе.
  return 'Запрос отправлен.';
}
